import { IconAlertTriangle, IconCircleCheck, IconClock, IconMail, IconRefresh } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

import Button, { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalContentPadding, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

type Props = { isOpen: boolean; setOpen(value: boolean): void }

const CustomEmailModal: FC<Props> = ({ isOpen, setOpen }) => {
	const currentUser = useUser()
	const alert = useAlert()

	const [email, setEmail] = useState("")

	const {
		data,
		error,
		refetch,
		isPending,
		isFetching: isCheckingVerification,
	} = query.platform.getCustomEmailSender.useQuery(undefined, {
		enabled: isOpen,
	})

	const create = query.platform.setCustomEmailSender.useMutation({
		async onSuccess(result) {
			if (result.status === "shared-mailbox-domain") {
				await alert.show(
					"Unsupported sender address",
					"Gmail, Outlook and other shared mailbox providers can't be used as a sender address, since they don't let you add the DNS records that prove the domain is yours. Use an address on a domain you control the DNS for.",
				)
				return
			}
			if (result.status === "domain-taken") {
				await alert.show(
					"Domain already connected",
					`${domain} is already connected to another Forento academy. Contact support if the domain belongs to you.`,
				)
				return
			}
			await Promise.all([refetch(), currentUser.reload()])
		},
	})

	const remove = query.platform.deleteCustomEmailSender.useMutation({
		async onSuccess() {
			await Promise.all([refetch(), currentUser.reload()])
			setEmail("")
		},
	})

	useEffect(() => {
		if (!isOpen) return
		if (data) {
			setEmail(`${data.senderUsername}@${data.domain}`)
		} else if (data === null) {
			setEmail("")
		}
	}, [data, isOpen])

	const trimmedEmail = email.trim()
	const lastIndexOfAtSymbol = trimmedEmail.lastIndexOf("@")
	const senderUsername = lastIndexOfAtSymbol === -1 ? "" : trimmedEmail.slice(0, lastIndexOfAtSymbol)
	const domain = lastIndexOfAtSymbol === -1 ? "" : trimmedEmail.slice(lastIndexOfAtSymbol + 1)
	const isSubmitDisabled = senderUsername.length === 0 || domain.length === 0
	const hasExistingSender = data !== null && data !== undefined
	const isVerified = data?.isVerified === true
	const isPendingVerification = hasExistingSender && !isVerified
	const isBusy = create.isPending || remove.isPending
	const existingSenderAddress = data ? `${data.senderUsername}@${data.domain}` : ""
	const existingDomain = data?.domain ?? ""
	const displayDomain = existingDomain || domain
	const dkimTokens = data?.dkimTokens ?? []
	const hasLoaded = data !== undefined && !error

	return (
		<DefaultModal
			onClose={isBusy ? undefined : () => setOpen(false)}
			width={1000}
			isOpen={isOpen}
			onSubmit={() => {
				if (isSubmitDisabled) return
				create.mutate({
					domain,
					senderUsername,
				})
			}}
		>
			<ModalTitle>Custom email sender</ModalTitle>
			<ModalText>
				Send emails to your students from your own address. Connect the sender address, add the DNS records, and
				verify ownership when your DNS changes have propagated.
			</ModalText>
			{error ? (
				<ModalContentPadding>
					<div
						className="border-danger/20 bg-danger/5 text-danger flex items-start gap-3 rounded-lg border p-4"
						role="alert"
					>
						<IconAlertTriangle className="mt-0.5 size-5 shrink-0" />
						<div>
							<p className="text-sm font-semibold">Failed to load email sender settings</p>
							<p className="mt-1 text-sm text-red-700">Close this window and try again.</p>
						</div>
					</div>
				</ModalContentPadding>
			) : isPending ? (
				<PartialLoadingPage />
			) : (
				<ModalInputs>
					<div
						className={cn(
							"flex items-start gap-3 rounded-lg border p-4",
							isVerified && "border-success/20 bg-success/5 text-success",
							isPendingVerification && "border-amber-200 bg-amber-50 text-amber-700",
							!hasExistingSender && "border-gray-200 bg-gray-50 text-gray-700",
						)}
					>
						<div
							className={cn(
								"flex size-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm",
								isVerified && "text-success",
								isPendingVerification && "text-amber-600",
								!hasExistingSender && "text-primary",
							)}
						>
							{isVerified ? (
								<IconCircleCheck className="size-5" />
							) : isPendingVerification ? (
								<IconClock className="size-5" />
							) : (
								<IconMail className="size-5" />
							)}
						</div>
						<div className="min-w-0">
							<p className="text-sm font-semibold">
								{isVerified
									? "Verified and ready"
									: isPendingVerification
										? "Pending DNS verification"
										: "No sender connected"}
							</p>
							<p className="mt-1 text-sm leading-relaxed text-gray-600">
								{isVerified
									? `Emails can now be sent from ${existingSenderAddress}.`
									: isPendingVerification
										? `Add the DKIM records for ${existingDomain}, then check verification.`
										: "Enter the email address students should see as the sender."}
							</p>
						</div>
					</div>
					<InputField
						label="Sender email address"
						placeholder="noreply@example.com"
						value={email}
						onChange={setEmail}
						inputType="email"
						autoComplete="off"
					/>
					{hasExistingSender && (
						<div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
							<div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
								<div>
									<p className="text-sm font-semibold text-gray-900">DKIM records</p>
									<p className="mt-1 text-xs text-gray-500">
										Add these CNAME records at your DNS provider for{" "}
										<span className="font-semibold text-gray-700">{displayDomain}</span>.
									</p>
								</div>
								<span
									className={cn(
										"inline-flex w-fit items-center rounded-full px-2.5 py-1 text-xs font-medium",
										isVerified ? "bg-success/10 text-success" : "bg-amber-100 text-amber-700",
									)}
								>
									{isVerified ? "Verified" : "Awaiting verification"}
								</span>
							</div>
							{dkimTokens.length > 0 ? (
								<div className="overflow-x-auto">
									<table className="w-full min-w-170 border-collapse text-left text-sm">
										<thead>
											<tr className="border-b border-gray-200 text-gray-500">
												<th className="w-24 px-4 py-3 font-medium">Type</th>
												<th className="px-4 py-3 font-medium">Name</th>
												<th className="px-4 py-3 font-medium">Value</th>
											</tr>
										</thead>
										<tbody>
											{dkimTokens.map(token => (
												<tr key={token} className="border-b border-gray-100 last:border-b-0">
													<td className="px-4 py-3 align-top text-xs font-semibold text-gray-500">
														CNAME
													</td>
													<td className="px-4 py-3 align-top">
														<code className="block rounded bg-gray-50 px-2 py-1 font-mono text-xs break-all text-gray-800">
															{token}._domainkey.{displayDomain}
														</code>
													</td>
													<td className="px-4 py-3 align-top">
														<code className="block rounded bg-gray-50 px-2 py-1 font-mono text-xs break-all text-gray-800">
															{token}.dkim.amazonses.com
														</code>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<div className="px-4 py-6 text-sm text-gray-500">
									DNS records are not available yet. Try checking verification again in a moment.
								</div>
							)}
						</div>
					)}
				</ModalInputs>
			)}
			<ModalButtons>
				<Button variant="secondary" onClick={() => setOpen(false)} isDisabled={isBusy}>
					{hasExistingSender ? "Close" : "Cancel"}
				</Button>
				{hasExistingSender && (
					<Button
						variant="secondary-danger"
						onClick={() => remove.mutate()}
						isLoading={remove.isPending}
						isDisabled={create.isPending}
					>
						Disconnect
					</Button>
				)}
				{hasExistingSender && !isVerified && (
					<Button
						variant="primary"
						onClick={() => refetch()}
						isDisabled={create.isPending || remove.isPending}
						isLoading={isCheckingVerification}
					>
						<IconRefresh className="size-4" />
						Check verification
					</Button>
				)}
				{hasLoaded && !hasExistingSender && (
					<SubmitButton
						variant="primary"
						isDisabled={isSubmitDisabled || isBusy}
						isLoading={create.isPending}
					>
						Connect
					</SubmitButton>
				)}
				{hasExistingSender && !error && (
					<SubmitButton
						variant="primary"
						isDisabled={isSubmitDisabled || isBusy}
						isLoading={create.isPending}
					>
						Update
					</SubmitButton>
				)}
			</ModalButtons>
		</DefaultModal>
	)
}

export default CustomEmailModal
